import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { FilterModel, MediaAppListItemModel } from "../../models";
import { ScheduleListItemModel } from "src/app/scheduler/models";
import { BaseService } from "../../services";
import { AppUtils } from "src/helpers";
import { NzDrawerComponent } from "ng-zorro-antd/drawer";
import { Subscription } from "rxjs";
import { EnhancedSchedulerService, SchedulerListItemDto } from "src/app/scheduler/services/enhanced-scheduler.service";
import { SharedModule } from '../../../shared/shared.module';
import moment from 'moment';
import { Router } from "@angular/router";

@Component({
    standalone: true,
    imports: [SharedModule],
    selector: 'app-schedule-list-list',
    templateUrl: './schedule-list-list.component.html',
    styleUrls: ['./schedule-list-list.component.scss']
})

export class ScheduleListComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('drawer') drawer: NzDrawerComponent;
    @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef;
    @Input() groupId: number;
    @Input() addFromDisplay: boolean;
    @Input() displayId: number;
    @Input() selectedScheduleId: number;
    @Input() medias: Array<MediaAppListItemModel> = new Array<MediaAppListItemModel>();
    @Output() closeDrawer: EventEmitter<{ selectedSchedule: ScheduleListItemModel, updateValue: boolean }> = new EventEmitter<{ selectedSchedule: ScheduleListItemModel, updateValue: boolean }>();
    isModelLoaded: boolean;
    filterKey: string;
    model: Array<ScheduleListItemModel>;
    isLoading = true;
    sortOrder: string;
    hasSchedule: boolean = true;
    addSchedule: boolean;
    scheduleId: number;
    totalRecords: number = 0;
    recordsToSkip: number;
    pageSize: number;
    loadingMore = false;
    hasMore = true;
    page = 1;
    filterModel = new FilterModel();
    subscriptions = new Array<Subscription>();

    constructor(
        private baseService: BaseService,
        private appUtils: AppUtils,
        private enhancedSchedulerService: EnhancedSchedulerService,
        private router: Router,
    ) {
        this.filterModel.sortBy = 'name';
        this.filterModel.sortDir = 'asc';
    }

    ngOnInit(): void {
        this.getList(true);
    }

    ngAfterViewInit() {
        this.scrollContainer.nativeElement.addEventListener('scroll', this.onScroll.bind(this));
    }

    trackById(index: number, item: any): number {
        return item.id;
    }

    onScroll(event: any) {
        const element = this.scrollContainer.nativeElement;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        if (scrollTop + clientHeight >= scrollHeight * 0.8 && this.hasMore && !this.loadingMore) {
            this.getMoreRecords();
        }
    }

    getList(initialLoading = false, limit = 15) {
        this.isModelLoaded = false;
        this.isLoading = true;
        this.filterModel.limit = limit;
        this.filterModel.offset = 0;
        this.totalRecords = 0;
        
        // Prepare filter model for API
        const apiFilterModel = {
            page: 1,
            pageSize: limit,
            searchTerm: this.filterModel.filterKey || undefined,
            sortBy: this.filterModel.sortBy || 'name',
            sortDirection: this.filterModel.sortDir || 'asc'
        };
        
        const requestSubscription = 
        this.enhancedSchedulerService.getSchedulerList(this.groupId, apiFilterModel)
            .subscribe({
                next: (response: Array<SchedulerListItemDto>) => {
                    // Map API response to ScheduleListItemModel
                    this.model = response.map(scheduler => {
                        const item = new ScheduleListItemModel();
                        item.id = scheduler.id;
                        item.name = scheduler.name;
                        item.eventCount = scheduler.eventCount || 0;
                        item.isSelected = this.selectedScheduleId != null && scheduler.id === this.selectedScheduleId;
                        return item;
                    });

                    this.totalRecords = this.model.length;

                    if (initialLoading) {
                        this.hasSchedule = this.model.length > 0;
                    }
                    this.isModelLoaded = true;
                    this.isLoading = false;
                },
                error: (error) => {
                    this.baseService.processErrorResponse(error);
                    this.isLoading = false;
                }
            });
            this.subscriptions.push(requestSubscription);
        
    }

    openScheduleUpsertDrawer() {
        this.addSchedule = true;
        this.drawer.open();
    }

    addNewSchedule(): void {
        // Create schedule payload with timestamp to prevent duplicate names
        const timestamp = `${moment().format('YYYY-MM-DD hh:mm:ss')}`;
        const uniqueName = `New Schedule - ${timestamp}`;

        const schedulePayload = {
            name: uniqueName,
            description: '',
            groupId: this.groupId || this.appUtils.getGroupId() || null,
            folderId: null,
            userId: this.appUtils.getUserId() || null,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            displayIds: [this.displayId].filter(id => id !== undefined),
        };

        this.enhancedSchedulerService.createSchedule(schedulePayload).subscribe({
            next: (response) => {
                const queryParams: any = {
                    scheduleId: response.id,
                    scheduleName: response.name,
                };

                // Attach the current screen/display by default if displayId is available
                if (this.displayId) {
                    queryParams.screenId = this.displayId;
                }

                this.router.navigate(['/scheduler/create'], {
                    queryParams: queryParams,
                });
            },
            error: (error) => {
                console.error('Error creating schedule:', error);
                this.baseService.processErrorResponse(error);
            },
        });
    }

    close(): void {
        this.addSchedule = false;
    }

    closeScheduleUpsertDrawer(event: any): void {
        if (event.scheduleAdded) {
            this.scheduleId = event.scheduleId;
            this.getList(true);
        }
        this.drawer.close();
        this.addSchedule = false;
    }

    onSearch() {
        if (this.filterModel.filterKey) {
            this.getList();
        }
    }

    clearSearch() {
        this.filterModel.filterKey = '';
        this.getList();
    }

    selectSchedule(item: ScheduleListItemModel): void {
        this.closeDrawer.emit({ selectedSchedule: item, updateValue: true });
    }

    cancel(): void {
        this.closeDrawer.emit({ selectedSchedule: new ScheduleListItemModel(), updateValue: false });
    }

    sortSchedule() {
        if (this.sortOrder === 'ascending') {
            this.filterModel.sortBy = 'name';
            this.filterModel.sortDir = 'asc';
        } else if (this.sortOrder === 'descending') {
            this.filterModel.sortBy = 'name';
            this.filterModel.sortDir = 'desc';
        }
        this.getList(false, this.totalRecords);
    }

    filterKeyChanged() {
        if (this.appUtils.isNullOrEmpty(this.filterModel.filterKey)) {
            this.getList();
        }
    }

    getMoreRecords() {
        if (this.loadingMore) { return; }
        this.filterModel.limit = 5;
        this.filterModel.offset = this.totalRecords;
        this.loadingMore = true;
        this.isLoading = true;
        
        // Prepare filter model for API
        const apiFilterModel = {
            page: Math.floor(this.totalRecords / 5) + 1,
            pageSize: 5,
            searchTerm: this.filterModel.filterKey || undefined,
            sortBy: this.filterModel.sortBy || 'name',
            sortDirection: this.filterModel.sortDir || 'asc'
        };
        
        this.enhancedSchedulerService.getSchedulerList(this.groupId, apiFilterModel)
            .subscribe({
                next: (response: Array<SchedulerListItemDto>) => {
                    if (response && response.length > 0) {
                        const newItems = response.map(scheduler => {
                            const item = new ScheduleListItemModel();
                            item.id = scheduler.id;
                            item.name = scheduler.name;
                            item.eventCount = scheduler.eventCount || 0;
                            item.isSelected = this.selectedScheduleId != null && scheduler.id === this.selectedScheduleId;
                            return item;
                        });
                        
                        newItems.forEach((x) => {
                            this.model.push(x);
                        });
                        this.totalRecords += newItems.length;
                    }
                    else {
                        this.hasMore = false;
                    }
                    this.loadingMore = false;
                    this.isLoading = false;
                },
                error: (error) => {
                    this.loadingMore = false;
                    this.isLoading = false;
                    this.baseService.processErrorResponse(error);
                }
            });
    }

    ngOnDestroy() {
        this.isLoading = false;
        this.subscriptions.forEach(s => { s.unsubscribe(); });
    }
}
